import React from 'react';
import { getOsInfo, PackerIcon, AmiIcon } from '../utils/os-icons';

/**
 * Renders the OS icon for a given blueprint name or OS type.
 * Falls back to a text label if no icon matches.
 *
 * @param {object} props
 * @param {string} props.name - Blueprint name (e.g. "ubuntu24.04-lts-arm64") or OS type
 * @param {number} [props.size=20] - Icon width/height in px
 * @param {boolean} [props.showLabel=false] - Show the OS display name next to the icon
 * @param {string} [props.className] - Additional CSS classes
 */
export default function OsIcon({ name, size = 20, showLabel = false, className = '', fallback = '\u2014' }) {
  const info = getOsInfo(name);

  // A missing name used to render an empty cell: showLabel produced
  // <span>{undefined}</span> and otherwise it returned null. Callers could not
  // tell "no blueprint" from "failed to load", so render an explicit fallback.
  if (!name) {
    return <span className={className} style={{ color: '#636e72' }}>{fallback}</span>;
  }

  if (!info) {
    return showLabel ? <span className={className}>{name}</span> : null;
  }

  const IconComponent = info.icon;

  return (
    <span className={`inline-flex items-center gap-1.5 ${className}`} title={info.label}>
      <IconComponent width={size} height={size} aria-hidden="true" />
      {showLabel && <span>{info.label}</span>}
    </span>
  );
}

/**
 * Packer build tool icon.
 */
export function PackerBuildIcon({ size = 20, showLabel = false, className = '' }) {
  return (
    <span className={`inline-flex items-center gap-1.5 ${className}`} title="Packer">
      <PackerIcon width={size} height={size} aria-hidden="true" />
      {showLabel && <span>Packer</span>}
    </span>
  );
}

/**
 * AWS AMI icon.
 */
export function AmiImageIcon({ size = 20, showLabel = false, className = '' }) {
  return (
    <span className={`inline-flex items-center gap-1.5 ${className}`} title="AMI">
      <AmiIcon width={size} height={size} aria-hidden="true" />
      {showLabel && <span>AMI</span>}
    </span>
  );
}
